/**
 * /recipes/shopping-list — auto-aggregated shopping list from every recipe
 * the user has added to their cart. Fully client-side (localStorage), no
 * backend calls required.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShoppingBasket, Trash2, Minus, Plus, Copy, Printer, ClipboardList, ChefHat,
} from "lucide-react";
import {
  loadShoppingList,
  removeRecipeFromShoppingList,
  updateServingsInShoppingList,
  clearShoppingList,
  aggregateShoppingList,
  shoppingListToText,
  shoppingListSize,
  groupByAisle,
} from "@/lib/recipeMath";

export default function ShoppingList() {
  const [list, setList] = useState(() => loadShoppingList());
  const [groupMode, setGroupMode] = useState("aisle"); // aisle | flat

  useEffect(() => {
    const h = () => setList(loadShoppingList());
    window.addEventListener("wofy-shopping-list-updated", h);
    window.addEventListener("storage", h);
    return () => {
      window.removeEventListener("wofy-shopping-list-updated", h);
      window.removeEventListener("storage", h);
    };
  }, []);

  const size = shoppingListSize(list);
  const agg = aggregateShoppingList(list);
  const { rows, orphans } = agg;
  const aisleGroups = groupByAisle(agg);

  const bumpServings = (slug, delta) => {
    const rec = list.recipes[slug];
    if (!rec) return;
    const next = Math.max(1, Math.min(200, (rec.servings_target || rec.servings_base) + delta));
    setList(updateServingsInShoppingList(slug, next));
  };

  const setServingsExact = (slug, value) => {
    const n = Math.max(1, Math.min(200, parseInt(value || "1", 10)));
    setList(updateServingsInShoppingList(slug, n));
  };

  const remove = (slug) => setList(removeRecipeFromShoppingList(slug));

  const clearAll = () => {
    if (!window.confirm("Clear the entire shopping list?")) return;
    clearShoppingList();
    setList({ recipes: {} });
    toast.success("Shopping list cleared");
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shoppingListToText(list));
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed — try Print instead");
    }
  };

  if (size === 0) {
    return (
      <div data-testid="shopping-list-empty" className="max-w-3xl mx-auto px-6 py-16 text-center">
        <ShoppingBasket size={40} className="mx-auto opacity-40 mb-3" />
        <h1 className="font-display text-3xl mb-2">Your shopping list is empty.</h1>
        <p className="mb-6 text-sm max-w-md mx-auto" style={{ color: "var(--wofy-text-soft)" }}>
          Add recipes to your list from any Recipe page. Ingredients scale automatically
          when you change portions, and same-item quantities are combined.
        </p>
        <Link to="/recipes" className="wofy-btn-primary inline-flex">
          <ChefHat size={16} /> Browse recipes
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="shopping-list-page" className="max-w-[1100px] mx-auto px-6 lg:px-10 py-14">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">RECIPES · SHOPPING LIST</div>
          <h1 className="font-display text-4xl md:text-5xl leading-tight">Your shopping list.</h1>
          <p className="mt-2 text-sm max-w-2xl" style={{ color: "var(--wofy-text-soft)" }}>
            Aggregated from {size} recipe{size === 1 ? "" : "s"} in your cart. Change
            servings below to re-scale on the fly. Items are combined when the name and
            unit match; anything else (like "to taste") gets its own line.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={copyToClipboard} data-testid="shopping-copy" className="wofy-btn-ghost text-sm">
            <Copy className="w-4 h-4" /> Copy
          </button>
          <button onClick={() => window.print()} data-testid="shopping-print" className="wofy-btn-ghost text-sm">
            <Printer className="w-4 h-4" /> Print
          </button>
          <button onClick={clearAll} data-testid="shopping-clear" className="wofy-btn-ghost text-sm" style={{ color: "#B23A3A" }}>
            <Trash2 className="w-4 h-4" /> Clear all
          </button>
        </div>
      </div>

      <div className="mt-8 grid gap-6 md:grid-cols-[1fr_320px]">
        {/* Aggregated list */}
        <section className="wofy-card">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="overline inline-flex items-center gap-1.5">
              <ClipboardList className="w-3.5 h-3.5" /> AGGREGATED · {rows.length + orphans.length} ITEMS
            </div>
            <div className="flex gap-1 text-[10px] font-mono uppercase" data-testid="group-toggle">
              {[{ v: "aisle", label: "By aisle" }, { v: "flat", label: "Flat list" }].map((t) => (
                <button
                  key={t.v}
                  onClick={() => setGroupMode(t.v)}
                  data-testid={`group-${t.v}`}
                  className={`px-2.5 py-1 rounded-full ${groupMode === t.v ? "" : "hover:bg-[color:var(--wofy-surface)]"}`}
                  style={groupMode === t.v ? { background: "var(--wofy-primary)", color: "#FDFBF7" } : { border: "1px solid var(--wofy-border)" }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {groupMode === "flat" ? (
            <ul className="divide-y" style={{ borderColor: "var(--wofy-line)" }}>
              {rows.map((r, i) => (
                <ItemRow key={i} testid={`shopping-row-${i}`} row={r} />
              ))}
              {orphans.length > 0 && (
                <li className="pt-4 pb-1 font-mono text-[10px] uppercase tracking-wider opacity-70">
                  To taste · non-numeric
                </li>
              )}
              {orphans.map((o, i) => (
                <ItemRow key={`o-${i}`} row={o} />
              ))}
            </ul>
          ) : (
            <div className="space-y-6">
              {aisleGroups.map((g) => (
                <div key={g.aisle} data-testid={`aisle-${g.aisle.replace(/\W+/g, "-").toLowerCase()}`}>
                  <div className="font-display text-base mb-1 flex items-center gap-2">
                    <span
                      className="inline-block w-1.5 h-1.5 rounded-full"
                      style={{ background: "var(--wofy-primary)" }}
                    />
                    {g.aisle}
                    <span className="text-[10px] font-mono opacity-50">
                      · {g.rows.length + g.orphans.length}
                    </span>
                  </div>
                  <ul className="divide-y" style={{ borderColor: "var(--wofy-line)" }}>
                    {g.rows.map((r, i) => (
                      <ItemRow key={i} row={r} testid={`aisle-row-${g.aisle}-${i}`} />
                    ))}
                    {g.orphans.map((o, i) => (
                      <ItemRow key={`o-${i}`} row={o} />
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Recipes in cart with per-recipe portion scaler */}
        <aside className="space-y-3">
          <div className="overline">IN YOUR CART</div>
          {Object.values(list.recipes || {}).map((rec) => (
            <div key={rec.slug} data-testid={`shopping-recipe-${rec.slug}`} className="wofy-card">
              <div className="flex items-start gap-3">
                {rec.cover_photo && (
                  <img src={rec.cover_photo} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <Link to={`/recipes/${rec.slug}`} className="font-display text-base leading-tight hover:underline block">
                    {rec.title}
                  </Link>
                  <div className="mt-1 flex items-center gap-1 text-xs">
                    <button onClick={() => bumpServings(rec.slug, -1)} aria-label="fewer servings" className="w-6 h-6 rounded-full grid place-items-center border" style={{ borderColor: "var(--wofy-line)" }}>
                      <Minus size={10} />
                    </button>
                    <input
                      type="number" min={1} max={200}
                      value={rec.servings_target || rec.servings_base}
                      onChange={(e) => setServingsExact(rec.slug, e.target.value)}
                      className="w-12 px-1 py-0.5 rounded border text-center"
                      style={{ borderColor: "var(--wofy-line)", background: "transparent" }}
                    />
                    <button onClick={() => bumpServings(rec.slug, +1)} aria-label="more servings" className="w-6 h-6 rounded-full grid place-items-center border" style={{ borderColor: "var(--wofy-line)" }}>
                      <Plus size={10} />
                    </button>
                    <span className="opacity-60 ml-1">servings</span>
                  </div>
                </div>
                <button onClick={() => remove(rec.slug)} aria-label="Remove from list" className="opacity-60 hover:opacity-100 shrink-0" data-testid={`shopping-remove-${rec.slug}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </aside>
      </div>
    </div>
  );
}


function ItemRow({ row, testid }) {
  return (
    <li
      {...(testid ? { "data-testid": testid } : {})}
      className="py-2.5 flex items-start gap-3 text-sm"
      style={{ borderBottomColor: "var(--wofy-line)" }}
    >
      <input type="checkbox" className="mt-1" aria-label={`Mark ${row.name}`} />
      <span className="font-semibold whitespace-nowrap min-w-[80px]">
        {[row.quantity, row.unit].filter(Boolean).join(" ")}
      </span>
      <span className="flex-1">
        {row.name}
        {row.optional && <em className="text-xs opacity-70"> (optional)</em>}
      </span>
      <span className="text-[10px] font-mono opacity-50 whitespace-nowrap">
        {row.from.slice(0, 2).join(" · ")}{row.from.length > 2 ? ` +${row.from.length - 2}` : ""}
      </span>
    </li>
  );
}
